/**
 * useFloorPlanConfigTemplates — native re-implementation of the legacy
 * `FloorPlanTemplates.context` (MODULE-CONTRACT §6 / spec §6).
 *
 * The saved-org-templates feature is an INPUT mechanism (pick a template →
 * pre-fill the config), not durable module state. This hook consumes the EXISTING
 * query layer (`dataQueries/floorPlan.query.ts` — ALLOWED) and resolves the
 * role/workspace/target-user from PF-v2-native sources:
 *   - role + workspace ← `useUserData()` / `useAuth0()` (app-wide, ALLOWED)
 *   - targetUserId     ← the PF-v2 store (`targetUserId`; admin-on-behalf like PoA)
 * NO `components/pages/PurchaseFlow/*` imports (DoD grep gate).
 *
 * OQ-6 (admin-on-behalf): if the target org id isn't resolvable the admin path is
 * skipped and only the client-self / default templates show — flagged as a DF
 * (mirrors PoA E-4). Client-self is the happy path for the build.
 *
 * The apply-template WRITER lives in the component (it needs `patch` + the logo
 * hub) — this hook only loads `availableTemplates`.
 */

import { useEffect, useMemo, useState } from 'react'
import { TFunction } from 'i18next'
import { useTranslation } from 'react-i18next'

import {
  useAdminGetFloorPlanTemplateDetails,
  useAdminGetFloorPlanTemplates,
  useGetFloorPlanTemplateDetails,
  useGetFloorPlanTemplateLogo,
  useGetFloorPlanTemplates
} from 'dataQueries'
import { useUserData } from 'components/contexts/UserDataContext'
import { useAuth0 } from 'utils/auth'
import { createFileFromBlob, downloadLogo } from 'utils/helpers'
import type { FloorPlanTemplateDetailsDTO } from 'models/purchaseFlow'

import basic_2D from 'assets/img/floorplan/2D/2D_commercial_bw.webp'
import basic_3D from 'assets/img/floorplan/3D/3D_basic_variant1.webp'
import furniture_variant1 from 'assets/img/floorplan/3D/3D_furniture_variant1.webp'
import default_template_2D_neutral from 'assets/img/floorplan/default_template_2D_neutral.webp'

import { usePfStore } from '../../store'
import { FloorPlanColors, FloorPlanFileOrientation, FloorPlanFormat, FloorPlanRenderType, Orientations, Styles } from 'constants/floorPlan'
import type { FloorPlanConfigOptions } from 'constants/floorPlan'

export type CompositeTemplate = {
  id: string
  name: string
  renderType: FloorPlanRenderType
  imagePlaceholder: string
  config: FloorPlanConfigOptions
}

export const DEFAULT_TEMPLATE_2D_ID = 'default-template-2d'
export const DEFAULT_TEMPLATE_3D_ID = 'default-template-3d'

const basicFloorPlanSimpleConfig: FloorPlanConfigOptions = {
  style: FloorPlanColors.NEUTRAL,
  showFixtures: true,
  showFurniture: true,
  showRoomNames: true,
  showRoomArea: true,
}

const basicFloorPlan3DConfig: FloorPlanConfigOptions = {
  style: Styles.STYLE_1,
  showFixtures: true,
  showFurniture: true,
  orientation: Orientations.BIRD_VIEW,
}

export const getDefault2DPlanTemplate = (t: TFunction): CompositeTemplate => ({
  id: DEFAULT_TEMPLATE_2D_ID,
  config: basicFloorPlanSimpleConfig,
  name: t('floor_plan_step.templates.default_template_name_2d'),
  imagePlaceholder: default_template_2D_neutral,
  renderType: FloorPlanRenderType.RENDER_2D,
})

export const getDefault3DPlanTemplate = (t: TFunction): CompositeTemplate => ({
  id: DEFAULT_TEMPLATE_3D_ID,
  config: basicFloorPlan3DConfig,
  name: t('floor_plan_step.templates.default_template_name_3d'),
  imagePlaceholder: furniture_variant1,
  renderType: FloorPlanRenderType.RENDER_3D,
})

export interface UseFloorPlanConfigTemplates {
  availableTemplates: Record<string, CompositeTemplate>
  isFetching: boolean
}

export function useFloorPlanConfigTemplates(allow3D: boolean): UseFloorPlanConfigTemplates {
  const { t } = useTranslation(['purchase_flow'])
  const { currentUserWorkspace } = useUserData()
  const { roles } = useAuth0()

  // Admin-on-behalf target user (SF id) from the PF-v2 store. Null for client-self.
  const targetUserId = usePfStore((s) => s.targetUserId)

  // NOTE: role-conditional hook calls — replicated from the legacy context. `roles`
  // is stable for the session lifetime, so this never changes call order in
  // practice (legacy relied on the same invariant). Client-self path is the happy
  // path; admin path requires a resolvable target id (OQ-6 fallback → defaults only).
  /* eslint-disable react-hooks/rules-of-hooks -- role is stable for the session; legacy parity */
  const floorPlanTemplates = roles.isClient
    ? useGetFloorPlanTemplates()
    : (targetUserId ? useAdminGetFloorPlanTemplates(targetUserId) : undefined)
  /* eslint-enable react-hooks/rules-of-hooks */
  const floorPlanTemplateDetail = useGetFloorPlanTemplateDetails()
  const floorPlanAdminTemplateDetail = useAdminGetFloorPlanTemplateDetails()
  const floorPlanTemplateLogo = useGetFloorPlanTemplateLogo()

  const [templateDetails, setTemplateDetails] = useState<FloorPlanTemplateDetailsDTO[]>([])

  const organizationTemplates: Record<string, CompositeTemplate> = useMemo(() => {
    const templates: Record<string, CompositeTemplate> = {}

    for (const template of templateDetails) {
      // Skip 3D templates if not allowed.
      if (!allow3D && template.renderType === FloorPlanRenderType.RENDER_3D) continue

      // The source model types these fields with the LEGACY enums; cast to the
      // local (byte-identical) enums at this single boundary.
      const floorPlanFormat = template.outputFileFormat.reduce((obj: Record<string, FloorPlanFormat>, format) => {
        const key = String(format) as keyof typeof FloorPlanFormat
        if (FloorPlanFormat[key]) obj[FloorPlanFormat[key]] = FloorPlanFormat[key]
        return obj
      }, {})

      const orientation =
        template.renderType === FloorPlanRenderType.RENDER_3D
          ? (template.orientation3d as unknown as Orientations | undefined)
          : undefined
      const defaultPlaceholder = template.renderType === FloorPlanRenderType.RENDER_2D ? basic_2D : basic_3D

      templates[template.id] = {
        id: template.id,
        name: template.templateName,
        renderType: template.renderType,
        imagePlaceholder: template.previewImageUrl ?? defaultPlaceholder,
        config: {
          style: template.style,
          orientation,
          showFixtures: template.fixtures,
          showFurniture: template.furniture,
          showRoomNames: template.roomNames,
          showGardensAndRoofSlopes: template.gardensAndRoofSlopes,
          fileOrientation: new Set((template.fileOrientation ?? []) as unknown as FloorPlanFileOrientation[]),
          isMultiStory: template.isMultiStory,
          showScalebar: template.scaleBar,
          showCompass: template.compass,
          companyLogo: template.companyLogoId,
          outputLanguage: template.outputFileLanguage,
          showDimensionChain: template.showDimensionChain,
          showRoomArea: template.showEachRoomArea,
          floorPlanFormat,
          templateLogoFile: template.templateLogoFile,
        },
      }
    }

    return templates
  }, [allow3D, templateDetails])

  const availableTemplates = useMemo(() => {
    const templates = { ...organizationTemplates }

    templates[DEFAULT_TEMPLATE_2D_ID] = getDefault2DPlanTemplate(t)
    if (allow3D) templates[DEFAULT_TEMPLATE_3D_ID] = getDefault3DPlanTemplate(t)

    return templates
  }, [organizationTemplates, t, allow3D])

  useEffect(() => {
    if (!floorPlanTemplates) return

    floorPlanTemplates.data?.forEach(async (template) => {
      const data = roles.isClient
        ? await floorPlanTemplateDetail.mutateAsync({ templateId: template.templateId })
        : (targetUserId && await floorPlanAdminTemplateDetail.mutateAsync({ templateId: template.templateId, userId: targetUserId }))

      if (!data) return
      const details = data.data

      let logoFile: File | undefined

      try {
        if (details.companyLogoId) {
          const logoData = await floorPlanTemplateLogo.mutateAsync({
            logoId: details.companyLogoId,
            workspaceId: currentUserWorkspace?.id ?? '',
          })
          if (logoData.data) {
            const blob = await downloadLogo(logoData.data.signedURL, logoData.data.headers)
            logoFile = createFileFromBlob(blob, logoData.data.filename)
          }
        }
      } catch (error) {
         
        console.error('Failed to fetch template logo:', error)
      }

      if (details) setTemplateDetails((prev) => [...prev, { ...details, templateLogoFile: logoFile }])
    })

    // Omit the mutation deps to prevent a fetch loop (legacy parity).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [floorPlanTemplates?.data])

  return {
    availableTemplates,
    isFetching: !!floorPlanTemplates?.isFetching,
  }
}
